import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { ref } from 'vue';
import { loadPdfJs } from '@/lib/pdfjsClient';
import {
    extractTextCharactersFromPage,
    extractTextLinesFromPage,
    extractTextWordsFromPage,
} from '@/lib/pdfTextLines';
import type { PdfTextRegion } from '@/lib/pdfTextLines';

function isBenignRenderInterruption(e: unknown): boolean {
    if (
        typeof e === 'object' &&
        e !== null &&
        'name' in e &&
        (e as { name: string }).name === 'RenderingCancelledException'
    ) {
        return true;
    }

    const message = e instanceof Error ? e.message : String(e);

    return (
        message.includes('Rendering cancelled') ||
        message.includes('Cannot use the same canvas during multiple render()')
    );
}

const PREVIEW_ZOOM_MIN = 50;
const PREVIEW_ZOOM_MAX = 200;
const PREVIEW_ZOOM_DEFAULT = 100;

export function usePdfPage() {
    const isLoading = ref(false);
    const error = ref('');
    const pageCount = ref(0);
    const pageWidthPt = ref(0);
    const pageHeightPt = ref(0);
    const previewZoomPct = ref(PREVIEW_ZOOM_DEFAULT);

    let pdfDoc: PDFDocumentProxy | null = null;
    /** Bumps whenever a newer render starts — avoids concurrent renders overwriting the same canvas */
    let renderGeneration = 0;
    /** PDF.js forbids two `render()` calls on the same canvas until the prior task is cancelled or done */
    let lastRenderTask: RenderTask | null = null;

    async function loadFile(file: File): Promise<void> {
        lastRenderTask?.cancel();
        lastRenderTask = null;
        renderGeneration++;
        isLoading.value = true;
        error.value = '';
        pdfDoc = null;
        pageCount.value = 0;
        previewZoomPct.value = PREVIEW_ZOOM_DEFAULT;

        try {
            const { getDocument, VerbosityLevel } = await loadPdfJs();

            pdfDoc = await getDocument({
                data: await file.arrayBuffer(),
                // Suppress font parse noise (e.g. "TT: undefined function: 32") in DevTools — still renders.
                verbosity: VerbosityLevel.ERRORS,
            }).promise;
            pageCount.value = pdfDoc.numPages;
        } catch {
            error.value = 'Could not read PDF';
        } finally {
            isLoading.value = false;
        }
    }

    function previewRenderWidth(baseMaxWidth: number): number {
        const clampedZoom = Math.min(
            PREVIEW_ZOOM_MAX,
            Math.max(PREVIEW_ZOOM_MIN, previewZoomPct.value),
        );

        return Math.max(120, Math.round(baseMaxWidth * (clampedZoom / 100)));
    }

    async function renderPage(
        canvas: HTMLCanvasElement,
        pageNum: number,
        baseMaxWidth = 560,
    ): Promise<void> {
        if (!pdfDoc) {
            return;
        }

        const maxWidth = previewRenderWidth(baseMaxWidth);
        const generation = ++renderGeneration;
        lastRenderTask?.cancel();
        lastRenderTask = null;
        isLoading.value = true;
        error.value = '';

        let startedRenderTask: RenderTask | null = null;

        try {
            const page = await pdfDoc.getPage(pageNum);

            if (generation !== renderGeneration || !pdfDoc) {
                return;
            }

            const raw = page.getViewport({ scale: 1 });
            const scale = maxWidth / raw.width;
            const vp = page.getViewport({ scale });
            canvas.width = vp.width;
            canvas.height = vp.height;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                error.value = 'Could not render page preview';

                return;
            }

            ctx.setTransform(1, 0, 0, 1, 0, 0);
            const renderTask = page.render({
                canvas,
                canvasContext: ctx,
                viewport: vp,
                background: 'rgb(255,255,255)',
            });
            startedRenderTask = renderTask;
            lastRenderTask = renderTask;
            await renderTask.promise;

            if (generation !== renderGeneration || !pdfDoc) {
                return;
            }

            // After bitmap is painted so overlays / watchers see matching canvas px size + PDF pt size
            pageWidthPt.value = raw.width;
            pageHeightPt.value = raw.height;
        } catch (e) {
            if (
                isBenignRenderInterruption(e) ||
                generation !== renderGeneration
            ) {
                return;
            }

            if (generation === renderGeneration) {
                error.value = 'Could not render page preview';
            }
        } finally {
            if (
                startedRenderTask !== null &&
                lastRenderTask === startedRenderTask
            ) {
                lastRenderTask = null;
            }

            if (generation === renderGeneration) {
                isLoading.value = false;
            }
        }
    }

    async function extractTextLines(pageNum: number): Promise<PdfTextRegion[]> {
        if (!pdfDoc) {
            return [];
        }

        try {
            const page = await pdfDoc.getPage(pageNum);

            return extractTextLinesFromPage(page, pageNum);
        } catch {
            return [];
        }
    }

    async function extractTextWords(pageNum: number): Promise<PdfTextRegion[]> {
        if (!pdfDoc) {
            return [];
        }

        try {
            const page = await pdfDoc.getPage(pageNum);

            return extractTextWordsFromPage(page, pageNum);
        } catch {
            return [];
        }
    }

    async function extractTextCharacters(
        pageNum: number,
    ): Promise<PdfTextRegion[]> {
        if (!pdfDoc) {
            return [];
        }

        try {
            const page = await pdfDoc.getPage(pageNum);

            return extractTextCharactersFromPage(page, pageNum);
        } catch {
            return [];
        }
    }

    function unload(): void {
        lastRenderTask?.cancel();
        lastRenderTask = null;
        renderGeneration++;
        pdfDoc = null;
        pageCount.value = 0;
        pageWidthPt.value = 0;
        pageHeightPt.value = 0;
        previewZoomPct.value = PREVIEW_ZOOM_DEFAULT;
        isLoading.value = false;
    }

    return {
        isLoading,
        error,
        pageCount,
        pageWidthPt,
        pageHeightPt,
        previewZoomPct,
        previewZoomMin: PREVIEW_ZOOM_MIN,
        previewZoomMax: PREVIEW_ZOOM_MAX,
        loadFile,
        renderPage,
        extractTextLines,
        extractTextWords,
        extractTextCharacters,
        unload,
    };
}
